import { buildComponent } from '#components'

import './announcement-bar.css'

type AutoplayPlayer = EventTarget & {
  start: () => void
  stop: () => void
}

type AnnouncementBarCarouselEl = HTMLElement & {
  player?: AutoplayPlayer
  cells: HTMLElement[]
}

const SLIDE_TEXT_MAX_LEN = 160

const selectors = {
  carousel: 'announcement-bar-carousel',
  toggleBtn: '[data-announcement-bar-autoplay-toggle]',
  labelEl: '[data-announcement-bar-autoplay-toggle-label]',
  playIcon: '[data-announcement-bar-autoplay-toggle-icon-play]',
  pauseIcon: '[data-announcement-bar-autoplay-toggle-icon-pause]',
  liveRegion: '[data-announcement-bar-live-region]',
  slideStatusTemplate: '[data-slide-status-template]'
}

const carouselTabIndexObservers = new WeakMap<HTMLElement, MutationObserver>()
const slideStatusCleanups = new WeakMap<HTMLElement, () => void>()

const normalizeSlideSummary = (text: string) => {
  const singleLine = text.replace(/\s+/g, ' ').trim()

  if (singleLine.length <= SLIDE_TEXT_MAX_LEN) return singleLine

  return `${singleLine.slice(0, SLIDE_TEXT_MAX_LEN - 1)}…`
}

const formatSlideStatus = (template: string, current: number, total: number, slideText: string) => {
  const summary = slideText.length > 0 ? normalizeSlideSummary(slideText) : ''

  return template
    .replace('{{ current }}', String(current))
    .replace('{{ total }}', String(total))
    .replace('{{ text }}', summary)
}

const bindAnnouncementSlideStatus = (component: HTMLElement) => {
  const carouselEl = component.querySelector(selectors.carousel) as AnnouncementBarCarouselEl | null
  const liveRegionEl = component.querySelector(selectors.liveRegion) as HTMLElement | null
  const template = component.getAttribute(selectors.slideStatusTemplate)

  if (!carouselEl || !liveRegionEl || !template) return

  const onSettle = (event: Event) => {
    const { index, cell } = (event as CustomEvent<{ index: number; cell: HTMLElement }>).detail
    const total = carouselEl.cells.length

    if (total <= 1) return

    liveRegionEl.textContent = formatSlideStatus(template, index + 1, total, cell?.textContent ?? '')
  }

  carouselEl.addEventListener('carousel:settle', onSettle)

  slideStatusCleanups.set(component, () => {
    carouselEl.removeEventListener('carousel:settle', onSettle)
    slideStatusCleanups.delete(component)
  })
}

const AnnouncementBar = buildComponent({
  name: 'announcement-bar-component',
  onConnected: (component) => {
    slideStatusCleanups.get(component)?.()
    bindAnnouncementSlideStatus(component)

    const carousel = component.querySelector(selectors.carousel) as AnnouncementBarCarouselEl | null
    const toggleBtn = component.querySelector(selectors.toggleBtn) as HTMLButtonElement | null

    if (!carousel || !toggleBtn) return

    const removeCarouselTabIndex = () => {
      if (carousel.hasAttribute('tabindex')) {
        carousel.removeAttribute('tabindex')
      }
    }

    removeCarouselTabIndex()

    const carouselTabIndexObserver = new MutationObserver(removeCarouselTabIndex)

    carouselTabIndexObserver.observe(carousel, { attributes: true, attributeFilter: ['tabindex'] })
    carouselTabIndexObservers.set(component, carouselTabIndexObserver)

    const labelEl = toggleBtn.querySelector(selectors.labelEl) as HTMLElement | null
    const playIcon = toggleBtn.querySelector(selectors.playIcon) as HTMLElement | null
    const pauseIcon = toggleBtn.querySelector(selectors.pauseIcon) as HTMLElement | null

    const setUi = (isPlaying: boolean) => {
      toggleBtn.setAttribute('aria-pressed', isPlaying ? 'true' : 'false')
      toggleBtn.classList.toggle('is-playing', isPlaying)

      if (labelEl) labelEl.textContent = isPlaying ? 'Pause announcements' : 'Play announcements'
      if (playIcon) playIcon.hidden = isPlaying
      if (pauseIcon) pauseIcon.hidden = !isPlaying
    }

    setUi(true)

    const bindPlayer = (attempt = 0) => {
      const player = carousel.player

      if (!player) {
        if (attempt < 10) requestAnimationFrame(() => bindPlayer(attempt + 1))

        return
      }

      player.addEventListener('player:start', () => setUi(true))
      player.addEventListener('player:stop', () => setUi(false))
    }

    bindPlayer()

    toggleBtn.addEventListener('click', () => {
      const player = carousel.player
      const isPlaying = toggleBtn.getAttribute('aria-pressed') === 'true'

      if (!player) return

      if (isPlaying) {
        player.stop()
      } else {
        player.start()
      }
    })
  },
  onDisconnected: (component) => {
    slideStatusCleanups.get(component)?.()
    carouselTabIndexObservers.get(component)?.disconnect()
    carouselTabIndexObservers.delete(component)
  }
})

declare global {
  interface HTMLElementTagNameMap {
    'announcement-bar-component': HTMLElement
  }
}

export default AnnouncementBar
